import {IWizardSavedState, WizardSavedState} from './wizardSavedState';
import {WizardFocusSerializer} from './wizardFocusSerializer.service';
import {INodeHistoryEngine, HistoryNodeNavigationEngineFactory} from './historyNodeNavigationEngine';
import {WizardFocus} from './wizardFocus';
import {IWizardNode} from './wizardNode';
import {INodeNavigationEngine, TreeNodeNavigationEngineFactory} from './treeNodeNavigationEngine';

export abstract class WizardNavigationService {

    private currentNodeChangedEventName = 'navigationWizard:currentNodeChanged';

    public nodeTemplate: IWizardNode[];
    protected navigationEngine: INodeNavigationEngine;
    protected historyEngine: INodeHistoryEngine;

    // tslint:disable-next-line:variable-name
    protected _currentNode: WizardFocus;

    public get currentNode(): WizardFocus {
        return this._currentNode;
    }

    public static $inject = ['$rootScope', '$state', '$log', '$q', '$injector', 'treeNodeNavigationEngineFactory', 'historyNavigationEngineFactory', 'wizardFocusSerializer'];

    constructor(protected $rootScope: ng.IRootScopeService,
                protected $state: ng.ui.IStateService,
                protected $log: ng.ILogService,
                protected $q: ng.IQService,
                protected $injector: any,
                protected navigationEngineFactory: TreeNodeNavigationEngineFactory,
                protected historyEngineFactory: HistoryNodeNavigationEngineFactory,
                protected serializer: WizardFocusSerializer) {
    }

    public initialize(): void {
        this._currentNode = undefined;
        this.nodeTemplate = this.defineWizardNodes();
        this.navigationEngine = this.navigationEngineFactory.getInstance(this.nodeTemplate);
        this.historyEngine = this.historyEngineFactory.getInstance();
    }

    /**
     * @description start the wizard from a saved state (for when a user returns to the web site and wishes to start where they left off)
     * NOTE:  If errors occur during restore, this method will throw exceptions.  The caller can then choose to either error out or call start() so that the user
     * does not perceive an error (the wizard just starts from the beginning instead of where they left off);
     */
    public restore(state: IWizardSavedState): void {
        this.initialize();
        const history = this.serializer.deserializeFocusArray(this.nodeTemplate, state.history);
        this.historyEngine.restoreHistory(history);
        // the node that was the current page is the last element on the history stack, so "go back" to nav to the current page
        this.goBack();
    }

    public goNext(): void {
        this.navigationEngine.getNextNode(this._currentNode).then((result: WizardFocus) => {
            this.$log.debug('tree nav', result);
            this.historyEngine.addToHistory(this._currentNode);
            this._currentNode = result;
            this.navigateToNode(result, NavigationDirection.Next);
        });
    }

    public saveCurrentState(): IWizardSavedState {
        const history = this.historyEngine.getHistoryList();
        history.push(this._currentNode);
        const result = new WizardSavedState();
        result.history = this.serializer.serializeFocusArray(history);
        return result;
    }

    /**
     * @description navigate back 1 page, or to the target page. If the previous page (or target) is in history but is no longer valid
     * (eg because a location was removed), go back to the next page before that
     * If at the beginning of the wizard, the onGoBackFromBeginningOfWizard abstract method will be called.
     * @param target Optional.  If specified, skip back to the specified page.  Target must be within the wizard history (must have been visited before)
     */
    public goBack(target?: WizardFocus): void {
        const node = this.historyEngine.goBack(target);
        if (!node) {
            // need to go back to before the wizard (let the child implementation handle this) and restart the wizard state
            this.goBackFromBeginningOfWizard();
            return;
        }
        this.navigateToNode(node, NavigationDirection.Back);
    }

    private goBackFromBeginningOfWizard() {
        this.initialize();
        this.onGoBackFromBeginningOfWizard();
        const direction = NavigationDirection.Restart;
        this.$rootScope.$broadcast(this.currentNodeChangedEventName, {currentNode: this._currentNode, direction});
    }

    // helper method because a lot of our submission wizard pages already have a "navigate" metaphor in place
    public navigate(direction: NavigationDirection): void {
        switch (direction) {
            case NavigationDirection.Back:
                this.goBack();
                break;
            case NavigationDirection.Next:
                this.goNext();
                break;
            default:
                this.$log.error('Wizard could not navigate because the direction passed was an invalid value', direction);
                break;
        }
    }

    private navigateToNode(focus: WizardFocus, direction: NavigationDirection): void {
        focus.node.subjectData = focus.subject;
        focus.node.params = focus.node.params || {};
        focus.node.params.subject = focus.subject;

        this._currentNode = focus;
        const wizardNode = focus.node;
        if (angular.isDefined(wizardNode.go))
            this.$injector.invoke(wizardNode.go);
        else
            this.go(wizardNode);
        // todo when we move up to TS 2.x with string enums, make this event name a constant
        this.$rootScope.$broadcast(this.currentNodeChangedEventName, {currentNode: this._currentNode, direction});
    }

    /**
     * @description Convenience function for hooking the wizardNodeChange event.  Not required, you can also subscribe on $rootscope
     * @returns The unregister function for the event.  ALWAYS unregister the event in your $scope.$destroy event by calling the function that is returned
     */
    public subscribeOnWizardNodeChange(listener: (event: ng.IAngularEvent, args: IWizardCurrentNodeChangedEventArgs) => void): () => void {
        return this.$rootScope.$on('navigationWizard:currentNodeChanged', listener);
    }

    public getHistory(): WizardFocus[] {
        return this.historyEngine.getHistoryList();
    }

    /** @description Execute action once for each node that will be displayed.
     *  @param action function(focus: wizardFocus) that is called for every node where isIncluded() returned true
     *  @return Returns Promise that is resolved when all nodes have been tested.
     */
    public forEachIncludedNode(action: (focus: WizardFocus) => void): ng.IPromise<any> {
        return this.navigationEngine.forEachIncludedNode(action);
    }

    public isIncluded(focus: WizardFocus): ng.IPromise<boolean> {
        return this.navigationEngine.isIncluded(focus);
    }

    /**
     * @description: Inheritors override this method to take custom action when using UI-router to navigtate to the specified node.
     * Default behavior is to just call $state.go().  (override if you want to pass parameters/etc)
     * Do NOT put other default wizard behavior here because this is an intended override point.
     */
    protected go(node: IWizardNode) {
        this.$state.go(node.stateName, {'wizardParams': node.params});
    }

    /**
     * @description Inheritors must override this method to produce an array of the nodes (pages) that should be in the wizard.
     * This method is called when the start() method on the wizard is called.
     */
    protected abstract defineWizardNodes(): IWizardNode[];

    /**
     * @description Inheritors must override this method to define behavior if the goBack service mthod is called when the wizard is
     * currently at the root node. (ie to redirect elsewhere, etc.)
     * This method is called when the goBack() method on the wizard is called and the wizard is already at the root node.
     */
    protected abstract onGoBackFromBeginningOfWizard();
}

export interface IWizardCurrentNodeChangedEventArgs {
    currentNode: WizardFocus;
    direction: NavigationDirection;
}

export enum NavigationDirection {
    Next,
    Back,
    Restart
}
